import { useUser } from '@/components/providers/user-provider';
import { PlannerConfigEntity } from '@/services/configurator/PlannerConfigTypes';
import { NotFoundError } from '@/services/fetch/NotFoundError';
import { useEffect, useCallback, useState } from 'react';
import { toast } from 'sonner';
import { PlannerPublisStagingList } from './planner-publish-list';
import { useEnv } from '@/components/providers/env-provider';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PlannerEnv } from '@/utils/env';
import {
  deleteAllSelected,
  getPlannerData,
  getPlannerProductionData,
  getPlannerStagingData,
  getStagingDataList,
  publishAllSelected,
  updateListAndInvalidate,
} from './services/services';
import { mergeWithDefaultMarkets } from './utils/utils';
import Auth0Client from '@/services/auth/auth0Client';

export default function usePlannerPublish(publishToEnv: PlannerEnv) {
  const { role } = useUser();
  const { env } = useEnv();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userEmail = Auth0Client.getInstance().getUserEmail();
  const [diffPlannerId, setDiffPlannerId] = useState<string>('');
  const [allSelected, setAllSelected] = useState<string[]>([]);
  const [stagingList, setStagingList] = useState<PlannerPublisStagingList[]>(
    [],
  );
  const [stagingData, setStagingData] = useState<PlannerConfigEntity[]>();
  const [prev, setPrev] = useState<PlannerConfigEntity>(
    {} as PlannerConfigEntity,
  );
  const [current, setCurrent] = useState<PlannerConfigEntity>(
    {} as PlannerConfigEntity,
  );

  const [publishAllLoading, setPublishAllLoading] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);

  const updateList = useCallback(() => {
    try {
      (async () => {
        const list = await updateListAndInvalidate(
          queryClient,
          env,
          role,
          userEmail,
        );
        setStagingList(list);
      })();
    } catch (error) {
      console.error('Unable to update staging list', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    if (!diffPlannerId) {
      return;
    }

    (async () => {
      const stageData = await getPlannerStagingData(env, diffPlannerId);
      try {
        if (stageData) {
          const prodData = await getPlannerProductionData(
            stageData.identifier,
            env,
          );
          if (prodData) {
            setPrev({
              ...prodData,
              marketData: mergeWithDefaultMarkets(prodData.marketData),
            } as PlannerConfigEntity);
            setCurrent({
              ...stageData,
              marketData: mergeWithDefaultMarkets(stageData.marketData),
            });
          } else {
            // No production data to compare with, so no need to call mergeWithInitialState
            setPrev({} as PlannerConfigEntity);
            setCurrent(prodData);
          }
        }
      } catch (error) {
        if (error instanceof NotFoundError) {
          setPrev({} as PlannerConfigEntity);
          setCurrent(stageData);
        }
        throw error;
      }
    })();
  }, [diffPlannerId]);

  useEffect(() => {
    getStagingDataList(env).then((stagingPlanners) => {
      setStagingData(stagingPlanners);
    });
  }, []);

  const deleteAllSelectedFunc = async () => {
    try {
      setDeleteAllLoading(true);
      await deleteAllSelected(allSelected, env);
      updateList();

      toast.success('All selected deleted successfully');
    } catch (error) {
      toast.error('Failed to delete all selected');
      console.error(error);
    } finally {
      setDeleteAllLoading(false);
    }
  };

  const publishAllSelectedFunc = async () => {
    try {
      setPublishAllLoading(true);
      await publishAllSelected(allSelected, publishToEnv);
      updateList();

      toast.success('All selected published successfully');
      navigate('/');
    } catch (error) {
      toast.error('Failed to publish all selected');
      console.error(error);
    } finally {
      setPublishAllLoading(false);
    }
  };

  return {
    stagingList,
    allSelected,
    setAllSelected,
    publishAllLoading,
    deleteAllLoading,
    deleteAllSelected: deleteAllSelectedFunc,
    publishAllSelected: publishAllSelectedFunc,
    current,
    setCurrent,
    prev,
    setPrev,
    getPlannerData,
    updateList,
    setDiffPlannerId,
    diffPlannerId,
    stagingData,
  };
}
